import { DataTableFacet } from '@optools/components/data-table/data-table-facet';
import { Spinner } from '@optools/components/loading/spinner';
import { Button } from '@optools/components/ui/button';
import { AuditSourceSystemMetadata } from '@optools/services/operator-gateway/types/generated/graphql';
import { omit } from 'es-toolkit';
import { Cog, IdCardLanyard, MailIcon, Server, Waypoints, XCircle } from 'lucide-react';

import { AuditFilters } from '../../schemas/audits';
import { filterOperationsBySourceSystems } from '../../utils';
import { AuditTableDateFilter } from './audit-table-date-filter';
import { AuditTableFilter } from './audit-table-filter';
import { AuditTableResourceTagFilter } from './audit-table-resource-tag-filter';
export interface AuditTableFiltersProps {
  auditSourceSystems: AuditSourceSystemMetadata[];
  filters: AuditFilters;
  loading: boolean;
  onResetFilters: () => void;
  onUpdateFilter: <TKey extends keyof AuditFilters>(key: TKey, value: AuditFilters[TKey]) => void;
}

export function AuditTableFilters({
  auditSourceSystems,
  filters,
  loading,
  onResetFilters,
  onUpdateFilter,
}: AuditTableFiltersProps) {
  const {
    email = '',
    endDate,
    instigator = '',
    operations = [],
    sourceSystems = [],
    startDate,
    tag,
    traceId = '',
  } = filters;

  const sourceSystemOptions = auditSourceSystems.map((sourceSystem) => ({
    label: sourceSystem.name,
    value: sourceSystem.name,
  }));

  const operationsOptions = filterOperationsBySourceSystems(sourceSystems, auditSourceSystems);
  const showResetButton =
    startDate || endDate || Object.keys(omit(filters, ['startDate', 'endDate'])).length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DataTableFacet
        icon={<Server className="size-3.5" />}
        options={sourceSystemOptions}
        selected={sourceSystems}
        title="Source System"
        onChange={(value) => onUpdateFilter('sourceSystems', value)}
      />

      <DataTableFacet
        icon={<Cog />}
        options={operationsOptions}
        selected={operations}
        title="Operation"
        onChange={(value) => onUpdateFilter('operations', value)}
      />

      <AuditTableFilter
        icon={<IdCardLanyard />}
        title="Instigator"
        value={instigator}
        onChange={(value) => onUpdateFilter('instigator', value)}
      />

      <AuditTableFilter
        icon={<MailIcon />}
        title="Email"
        value={email}
        onChange={(value) => onUpdateFilter('email', value)}
      />

      <AuditTableFilter
        icon={<Waypoints />}
        title="Trace ID"
        value={traceId}
        onChange={(value) => onUpdateFilter('traceId', value)}
      />

      <AuditTableResourceTagFilter tag={tag} onChange={(value) => onUpdateFilter('tag', value)} />

      <AuditTableDateFilter
        endDate={endDate}
        startDate={startDate}
        onDateChange={(startDate, endDate) => {
          onUpdateFilter('startDate', startDate);
          onUpdateFilter('endDate', endDate);
        }}
      />

      {showResetButton && (
        <Button className="text-xs" size="sm" variant="outline" onClick={onResetFilters}>
          <XCircle />
          Reset
        </Button>
      )}

      {loading && <Spinner variant="loader" />}
    </div>
  );
}
